// Cópia para a área de transferência de um texto que só fica pronto depois de uma requisição (ORCA-1925).

/**
 * O Safari só deixa escrever na área de transferência durante o clique: quando a resposta da API chega,
 * a permissão já acabou e o execCommand('copy') falha sem erro. Por isso a escrita começa no clique,
 * com um ClipboardItem que espera o texto. Deve ser chamada direto no handler do clique.
 * Resolve true se copiou e false se não (inclusive se `text` rejeitar); nunca rejeita.
 */
export function copyTextWhenReady(text: Promise<string>): Promise<boolean> {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        const item = new ClipboardItem({
            'text/plain': text.then((value) => new Blob([value], { type: 'text/plain' })),
        });
        return navigator.clipboard.write([item]).then(
            () => true,
            () => text.then(copyText, () => false)
        );
    }
    return text.then(copyText, () => false);
}

function copyText(text: string): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(text).then(
            () => true,
            () => execCommandCopy(text)
        );
    }
    return Promise.resolve(execCommandCopy(text));
}

function execCommandCopy(text: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // readonly evita abrir o teclado no iOS; fixed evita rolar a página até o fim.
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    // O iOS ignora o select() sozinho.
    textarea.setSelectionRange(0, text.length);
    try {
        return document.execCommand('copy');
    } catch (ex) {
        console.warn('Copy to clipboard failed.', ex);
        return false;
    } finally {
        document.body.removeChild(textarea);
    }
}
